import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2, RotateCcw } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

const SAMPLE_CASE_TEXT = "rapid prototype development tools make it easy to build visually polished, client-side browser interfaces in real time.";

export function TextCaseConverter() {
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setText(SAMPLE_CASE_TEXT);
  }, []);

  const handleCopy = () => {
    copyToClipboard(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const toTitleCase = (str: string) => {
    return str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  };

  const toSentenceCase = (str: string) => {
    return str.replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase());
  };

  const toAlternatingCase = (str: string) => {
    return str.split('').map((c, i) => i % 2 === 0 ? c.toLowerCase() : c.toUpperCase()).join('');
  };

  return (
    <ToolPageLayout
      title="Text Case Converter Online – Convert to Upper, Lower, Title Case"
      description="Convert text to UPPERCASE, lowercase, Title Case, Sentence case, and aLtErNaTiNg cAsE instantly with our free online tool."
      h1="Text Case Converter"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Paste or type your text into the input area.",
        "Click the conversion button (UPPERCASE, lowercase, etc.) to change the case.",
        "Copy your converted text."
      ]}
      faq={[
        { question: "How to convert to title case?", answer: "Paste your text, click 'Title Case', and the tool will automatically capitalize the first letter of each word." },
        { question: "Is this tool free?", answer: "Yes, our online text case converter is completely free to use." }
      ]}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setText(text.toUpperCase())} variant="outline" size="sm">UPPERCASE</Button>
          <Button onClick={() => setText(text.toLowerCase())} variant="outline" size="sm">lowercase</Button>
          <Button onClick={() => setText(toTitleCase(text))} variant="outline" size="sm">Title Case</Button>
          <Button onClick={() => setText(toSentenceCase(text))} variant="outline" size="sm">Sentence case</Button>
          <Button onClick={() => setText(toAlternatingCase(text))} variant="outline" size="sm">aLtErNaTiNg cAsE</Button>
        </div>

        <Textarea
          placeholder="Type or paste your text here to convert case..."
          className="min-h-[300px] text-base"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        <div className="flex gap-2">
          <Button onClick={handleCopy} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Text"}
          </Button>
          <Button onClick={() => setText(SAMPLE_CASE_TEXT)} variant="outline" className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Reset Sample
          </Button>
          <Button onClick={() => setText("")} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
